import { routeErrorMessages } from '../utils/apiErrors';
import { AxiosError } from 'axios';

type ApiRoute = keyof typeof routeErrorMessages;

interface ApiErrorOptions {
  /**
   * If true, will return user-friendly error message.
   * If false, will return technical error for logging.
   */
  userFriendly?: boolean;
  /** Optional context to add to error message */
  context?: string;
}

export function useApiError(): {
  getErrorMessage: (
    error: unknown,
    route: ApiRoute,
    options?: ApiErrorOptions,
  ) => string;
} {
  const getErrorMessage = (
    error: unknown,
    route: ApiRoute,
    options: ApiErrorOptions = {},
  ): string => {
    const { userFriendly = true, context } = options;

    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const routeConfig = routeErrorMessages[route];

      if (status && routeConfig) {
        const errorConfig = routeConfig.errors.find((e) => e.code === status);
        if (errorConfig) {
          return userFriendly
            ? errorConfig.message
            : `${route} failed with status ${status}: ${errorConfig.message}`;
        }
      }

      // Handle unexpected status codes
      if (status) {
        return userFriendly
          ? 'Ett oväntat fel uppstod. Försök igen senare.'
          : `${route} failed with unexpected status ${status}`;
      }
    }

    // Network or other errors
    return userFriendly
      ? 'Kunde inte ansluta till servern. Kontrollera din internetanslutning.'
      : `${route} failed: ${context ? `[${context}] ` : ''}${error instanceof Error ? error.message : String(error)}`;
  };

  return { getErrorMessage };
}
